import { useState } from "react";
import useFetchApiData from "./GetStudentList";
import { toast } from 'react-toastify';
import useSignalRChat from "Common/SingleRConnection/useSignalRChat";
import API from "Common/Client/endpoint";
import api from "Common/Client/Interceptor";

const useCounselorChat = () => {
  const personalId = localStorage.getItem("personalId");
  const token = localStorage.getItem("token");
  const role = localStorage.getItem("role");
  const { members, groups } = useFetchApiData();
  const [activeChats, setActiveChats] = useState([]);
  const [selectedChatId, setSelectedChatId] = useState(null);
  const [search, setSearch] = useState("");
  const [modelSearch, setModelSearch] = useState("");
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [loading, setLoading] = useState(false);
  const [mentorModelVisible, setMentorModelVisible] = useState(false);
  const [hoveredId, setHoveredId] = useState(null);
  const [chatMessages, setChatMessages] = useState([]);
  const [chatDetails, setChatDetails] = useState(null);
  const [conversations, setConversations] = useState([]);
  const [loader, setLoader] = useState(true);
  const [msgLoader, setMsgLoader] = useState(true);
  const [hubConnection, setHubConnection] = useState(null);

  //Single R
  useSignalRChat(setChatMessages, setConversations, selectedChatId, setLoader, setHubConnection);

  const startChat = async (student) => {
    try {
      const payload = { receiverId: student.id };

      const response = await api.post(API.chats.create, payload);
      const data = response.data;

      if (!data.conversationId) {
        throw new Error("Missing conversationId in response");
      }

      const newChat = {
        id: data.conversationId,
        name: student.name,
        type: "single",
        messages: [],
      };

      setActiveChats((prevChats) => {
        if (!prevChats.some((chat) => chat.id === newChat.id)) {
          return [...prevChats, newChat];
        }
        return prevChats;
      });

      setSelectedChatId(newChat.id);
      setIsModalVisible(false);
    } catch (error) {
      console.error("Error starting chat:", error);
    }
  };

  const startMentorGroupChat = async (mentorGroup) => {
    try {
      const payload = {
        mentorId: Number(personalId),
        groupId: mentorGroup.groupId,
      };

      const response = await api.post(API.chats.addMentorGroup, payload);
      const data = response.data;

      const newChat = {
        id: data.conversationId,
        name: mentorGroup.groupName,
        type: "group",
        messages: [],
      };

      setActiveChats((prevChats) => {
        if (!prevChats.some((chat) => chat.id === newChat.id)) {
          return [...prevChats, newChat];
        }
        return prevChats;
      });

      setSelectedChatId(newChat.id);

      setMentorModelVisible(false);
    } catch (error) {
      console.error("Error starting chat:", error);
    }
  };

  const sendMessage = async (chatId, message, file = null) => {
    try {
      setLoading(true);

      const formData = new FormData();
      formData.append("conversationId", chatId);
      formData.append("content", message || "");
      formData.append("messageType", file ? 2 : 1);

      if (file) {
        formData.append("attachment", file, file.name);
      }

      // // Debugging: Check what data is being sent
      // for (let [key, value] of formData.entries()) {
      //   console.log(`${key}:`, value)
      // }

      await api.post(API.chats.sendMessage, formData);

      setConversations((prevConversations) =>
        prevConversations.map((conv) =>
          conv.conversationId === chatId ? { ...conv, chatSubTitle: message || file.name } : conv
        )
      );
      fetchChatDetails(chatId);
    } catch (error) {
      console.log("error", error);
      console.error("Error sending message:", error);
    } finally {
      setLoading(false);
    }
  };

  const allowedExtensions = [
    "pdf",
    "PDF",
    "doc",
    "DOC",
    "docx",
    "DOCX",
    "xls",
    "XLS",
    "xlsx",
    "XLSX",
    "CSV",
    "csv",
  ];
  const allowedMimeTypes = [
    "application/pdf",
    "application/msword",
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    "application/vnd.ms-excel",
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    "text/csv",
  ];

  const maxFileSize = 10 * 1024 * 1024; // 10MB

  const handleFileUpload = (file) => {
    if (!file || !file.name) {
      toast.error("Invalid file. Please upload a valid file.");
      return false;
    }

    // Extract file extension safely
    const fileExtension = file.name.split(".").pop().toLowerCase();

    // Check both extension and MIME type
    if (!allowedExtensions.includes(fileExtension) && !allowedMimeTypes.includes(file.type)) {
      toast.error("Invalid file type. Allowed types: PDF, DOC, DOCX, XLS, XLSX, CSV");
      return false;
    }

    if (file.size > maxFileSize) {
      toast.error("File size exceeds 10MB limit");
      return false;
    }

    return true;
  };

  const fetchChatDetails = async (conversationId) => {
    try {
      // The parent endpoint now returns conversation metadata only; the
      // messages list was moved to a separate paginated endpoint.
      const [metaResponse, listResponse] = await Promise.all([
        api.get(API.chats.getMessage(conversationId)),
        api.get(API.chats.getMessagesList(conversationId)).catch(() => ({ data: { items: [] } })),
      ]);

      const data = metaResponse.data;
      const listData = listResponse.data || { items: [] };
      const messages = listData.items || [];
      const canUploadAttachment = data.canUploadAttachment ?? false;
      const formattedMessages = messages.map((msg) => {
        const sender = (data.members || []).find((member) => member.userId === msg.senderId);

        return {
          text: msg.content,
          messageId: msg.messageId,
          attachmentUrl: msg.attachmentUrl,
          attachmentName: msg.attachmentName,
          messageType: msg.messageType,
          senderId: msg.senderId,
          senderName: sender ? sender.name : "Unknown",
          senderProfile: sender ? sender.profileUrl : "",
          isSentByUser: msg.isSentByUser,
        };
      });

      setChatDetails({
        id: data.conversationId,
        name: data.chatTitle,
        type: data.chatType === 2 ? "group" : "single",
        members: data.members || [],
        canUploadAttachment,
      });
      setChatMessages(formattedMessages);
      if (hubConnection) {
        await hubConnection.invoke("SetActiveConversation", conversationId);
        // console.log(' Active conversation set:', conversationId)
      }
    } catch (error) {
      console.error("Error fetching chat details:", error);
    } finally {
      setMsgLoader(false);
    }
  };

  return {
    handleFileUpload,
    hubConnection,
     personalId,
    token,
    members,
    groups,
    activeChats,
    isModalVisible,
    loading,
    mentorModelVisible,
    chatMessages,
    chatDetails,
    startChat,
    sendMessage,
    hoveredId,
    setHoveredId,
    search,
    setSearch,
    modelSearch,
    setModelSearch,
    setIsModalVisible,
    setMentorModelVisible,
    setSelectedChatId,
    selectedChatId,
    fetchChatDetails,
    setChatDetails,
    setChatMessages,
    loader,
    conversations,
    msgLoader,
    role,
    startMentorGroupChat,
  };
};

export default useCounselorChat;
